/* eslint-disable class-methods-use-this, no-return-assign */
import Promise from 'bluebird';
import { PlayerTech } from '../../constants';
import PlayerBase from '../PlayerBase';
import Loader from './Loader';
import * as perf from 'lib/util/perf';

class BAMPlayer extends PlayerBase {
    constructor({
        sdkHost,
        env = 'beta',
        playerVersion,
        geoTimeoutInSeconds = 7,
        loader
    } = {}) {
        super();
        this._sdkHost = sdkHost;
        this._playerVersion = playerVersion;
        this._extensionConfig = { environment: env };
        this._geoTimeoutInSeconds = geoTimeoutInSeconds;
        this._loader = BAMPlayer.loader || loader;
        this.getPlayerExtension = this.getPlayerExtension.bind(this);
    }

    get tech() {
        return PlayerTech.BAM;
    }

    get playerExtension() {
        return this._playerExtension;
    }

    get playerId() {
        return this._playerId;
    }

    get container() {
        return document.querySelector(this._playerId);
    }

    getPlayerExtension(BamVideoExtension) {
        if (!this._playerExtension) {
            this._playerExtension = new BamVideoExtension(this._extensionConfig); // eslint-disable-line new-cap
        }
        return this._playerExtension;
    }

    createSDKWrapper(options = {}) {
        const geolocationKey = 'geolocation';
        const subscriptionsKey = 'subscriptions';
        const sessionTokenKey = 'sessionToken';
        const userTrackingKey = 'userTrackingInformation';
        const { requests, timeout, transactionId } = options;
        if (!BAMPlayer.SDKData[transactionId]) {
            BAMPlayer.SDKData[transactionId] = {};
        } else {
            const transactionData = BAMPlayer.SDKData[transactionId];
            if (transactionData[geolocationKey]) {
                this.emit(BAMPlayer.Events.GEOLOCATION_READY, transactionData[geolocationKey]);
            }
            if (transactionData[subscriptionsKey]) {
                this.emit(BAMPlayer.Events.SUBSCRIPTIONS_READY, transactionData[subscriptionsKey]);
            }
            if (transactionData[sessionTokenKey]) {
                this.emit(BAMPlayer.Events.SESSIONTOKEN_READY, transactionData[sessionTokenKey]);
            }
            if (transactionData[userTrackingKey]) {
                this.emit(BAMPlayer.Events.USER_TRACKING_INFORMATION_READY, transactionData[userTrackingKey]);
            }
            if (Object.keys(transactionData).length === 2) {
                return Promise.resolve();
            }
        }
        const context = requests.join();
        const pertName = `BAMPlayer/createSDKWrapper_${context}`;
        return BAMPlayer.SDKPromises[transactionId] ||
            (BAMPlayer.SDKPromises[transactionId] = this.loadPlayer(context).then(playerExtension => {
                const { eventBus, events } = playerExtension;
                const geoEventName = events.get(BAMPlayer.Events.GEOLOCATION_READY);
                const subEventName = events.get(BAMPlayer.Events.SUBSCRIPTIONS_READY);
                const sessionTokenEventName = events.get(BAMPlayer.Events.SESSIONTOKEN_READY);
                const userTrackingEventName = events.get(BAMPlayer.Events.USER_TRACKING_INFORMATION_READY);
                let timer;
                const geoCallback = event => {
                    perf.measureAndTime(pertName);
                    window.clearTimeout(timer);
                    eventBus.off(geoEventName, geoCallback);
                    BAMPlayer.SDKData[transactionId][geolocationKey] = event;
                    this.emit(BAMPlayer.Events.GEOLOCATION_READY, event);
                };
                const subCallback = event => {
                    perf.measureAndTime(pertName);
                    window.clearTimeout(timer);
                    eventBus.off(subEventName, subCallback);
                    BAMPlayer.SDKData[transactionId][subscriptionsKey] = event;
                    this.emit(BAMPlayer.Events.SUBSCRIPTIONS_READY, event);
                };
                const tokenCallback = event => {
                    perf.measureAndTime(pertName);
                    window.clearTimeout(timer);
                    eventBus.off(sessionTokenEventName, tokenCallback);
                    BAMPlayer.SDKData[transactionId][sessionTokenKey] = event;
                    this.emit(BAMPlayer.Events.SESSIONTOKEN_READY, event);
                };

                const userTrackingCallback = event => {
                    perf.measureAndTime(pertName);
                    window.clearTimeout(timer);
                    eventBus.off(userTrackingEventName, userTrackingCallback);
                    BAMPlayer.SDKData[transactionId][userTrackingKey] = event;
                    this.emit(BAMPlayer.Events.USER_TRACKING_INFORMATION_READY, event);
                };

                // TODO: DTC - BAM to reject promise when any error occurs
                if (timeout) {
                    timer = window.setTimeout(() => {
                        console.error(`createSDKWrapper timed out after ${this._geoTimeoutInSeconds} seconds`);
                        perf.measureAndTime(pertName);
                        eventBus.off(geoEventName, geoCallback);
                        eventBus.off(subEventName, subCallback);
                        eventBus.off(sessionTokenEventName, tokenCallback);
                        eventBus.off(userTrackingEventName, userTrackingCallback);

                        if (requests.indexOf(geolocationKey) > -1) {
                            const geoEvent = { error: 'timeout' };
                            BAMPlayer.SDKData[transactionId][geolocationKey] = geoEvent;
                            this.emit(BAMPlayer.Events.GEOLOCATION_READY, geoEvent);
                        }
                        if (requests.indexOf(subscriptionsKey) > -1) {
                            const subEvent = {
                                error: 'timeout',
                                body: { subscriptions: [] }
                            };
                            BAMPlayer.SDKData[transactionId][subscriptionsKey] = subEvent;
                            this.emit(BAMPlayer.Events.SUBSCRIPTIONS_READY, subEvent);
                        }
                        if (requests.indexOf(sessionTokenKey) > -1) {
                            const tokenEvent = {
                                error: 'timeout',
                                body: { token: null }
                            };
                            BAMPlayer.SDKData[transactionId][sessionTokenKey] = tokenEvent;
                            this.emit(BAMPlayer.Events.SESSIONTOKEN_READY, tokenEvent);
                        }
                        if (requests.indexOf(userTrackingKey) > -1) {
                            const userTrackingEvent = {
                                error: 'timeout',
                                body: {}
                            };
                            BAMPlayer.SDKData[transactionId][userTrackingKey] = userTrackingEvent;
                            this.emit(BAMPlayer.Events.USER_TRACKING_INFORMATION_READY, userTrackingEvent);
                        }
                    }, this._geoTimeoutInSeconds * 1000);
                }

                if (requests.indexOf(geolocationKey) > -1) {
                    eventBus.on(geoEventName, geoCallback);
                }
                if (requests.indexOf(subscriptionsKey) > -1) {
                    eventBus.on(subEventName, subCallback);
                }
                if (requests.indexOf(sessionTokenKey) > -1) {
                    eventBus.on(sessionTokenEventName, tokenCallback);
                }
                if (requests.indexOf(userTrackingKey) > -1) {
                    eventBus.on(userTrackingEventName, userTrackingCallback);
                }
                perf.markStart(pertName);
                return playerExtension.createSDKWrapper(options.selector, options);
            }));
    }

    renderPaywall(options) {
        return this.loadPlayer().then(playerExtension => {
            const { eventBus, events } = playerExtension;
            return playerExtension.renderPaywall(options.selector, options)
                .then(() => {
                    eventBus.once(events.get(BAMPlayer.Events.LOGIN_REQUESTED), (event) => {
                        this.emit(BAMPlayer.Events.LOGIN_REQUESTED, event);
                    });
                });
        });
    }

    sendContentReward(selector, rewardToken, contentId, userActivityAction, recommendedContentIds) {
        return this.loadPlayer()
            .then(playerExtension => playerExtension.sendContentReward(
                selector,
                rewardToken,
                contentId,
                userActivityAction,
                recommendedContentIds
            ).then(result =>
                // TODO: Will be removed once deployed to production
                console.info('sendContentReward result:', result) // eslint-disable-line
            )
        );
    }

    loadPlayer(context) {
        const perfName = context ? `BAMPlayer/loadPlayer_${context}` : 'BAMPlayer/loadPlayer';
        perf.markStart(perfName);
        return this._loader.load(this._sdkHost, this._playerVersion).then((BamVideoExtension) => {
            perf.measureAndTime(perfName);
            return this.getPlayerExtension(BamVideoExtension);
        });
    }

    create(options) {
        // only allow one player within `WatchESPNWebPlayer` instance
        // TODO: consider using `singlePlayerMode=true`
        const isNew = !this._playerId;
        return this.loadPlayer()
            .then(playerExtension => {
                const { eventBus, events } = playerExtension;
                // or is `updatePlayerConfigs` for updating `extensionConfig`?
                // then how do we switch content?
                const api = isNew ? 'createPlayer' : 'loadMedia';
                return new Promise((resolve, reject) => {
                    console.log(api, options); // eslint-disable-line no-console
                    playerExtension[api](options.selector, options)
                        .then(playerId => {
                            if (isNew) {
                                this._playerId = playerId;
                                this.subscribe(playerExtension);
                                const readyTimer = window.setTimeout(() => {
                                    resolve(this);
                                }, 100);
                                eventBus.once(events.get(BAMPlayer.Events.EMBED_PLAYER_READY), () => {
                                    window.clearTimeout(readyTimer);
                                    resolve(this);
                                });

                                return playerExtension.insertPlayer(playerId);
                            }

                            resolve(this);

                            return this._playerId;
                        })
                        .catch(err => {
                            console.error(err);
                            reject(err);
                        });
                });
            });
    }

    loadMedia(options) {
        if (!this._playerExtension || !this._playerId) {
            return Promise.resolve(this);
        }
        return this._playerExtension.loadMedia(this._playerId, options).then(() => this);
    }

    destroyPlayer() {
        if (this._playerExtension && this._playerId) {
            this.unsubscribe();
            return this._playerExtension
                .destroyPlayer(this._playerId)
                .then(() => {
                    this._playerId = null;
                    return this;
                })
                .catch(() => {});
        }

        return Promise.resolve(this);
    }

    changeFeed(options) {
        if (!this._playerExtension || !this._playerId) {
            return Promise.resolve(this);
        }
        console.log('changeFeed', options); // eslint-disable-line no-console
        return this._playerExtension.changeFeed(this._playerId, options).then(() => this);
    }

    deactivate() {
        if (!this._playerExtension || !this._playerId) {
            return Promise.resolve(this);
        }
        return this._playerExtension.deactivatePlayer(this._playerId).then(() => this);
    }

    delete() {
        if (this._playerExtension && this._playerId) {
            this.unsubscribe();
            return this._playerExtension
                .deletePlayer(this._playerId)
                .then(() => {
                    this._playerId = null;
                    return this;
                })
                .catch(() => {});
        }
        return Promise.resolve(this);
    }

    play() {
        if (!this._playerExtension) {
            return Promise.resolve(this);
        }
        return this._playerExtension.playPlayer(this._playerId).then(() => this);
    }

    pause() {
        if (!this._playerExtension) {
            return Promise.resolve(this);
        }
        return this._playerExtension.pausePlayer(this._playerId).then(() => this);
    }

    mute() {
        if (!this._playerExtension) {
            return Promise.resolve(this);
        }
        return this._playerExtension.mutePlayer(this._playerId).then(() => this);
    }

    unmute() {
        if (!this._playerExtension) {
            return Promise.resolve(this);
        }
        return this._playerExtension.unmutePlayer(this._playerId).then(() => this);
    }

    error() {
        throw new Error('`error` not implemented');
    }

    getPlayerConfigs() {
        if (!this._playerExtension) {
            return Promise.resolve(this);
        }
        return this._playerExtension.getPlayerConfigs(this._playerId);
    }

    updatePlayerConfigs(newConfigs) {
        if (!this._playerExtension) {
            return Promise.resolve(this);
        }
        return this._playerExtension.updatePlayerConfigs(this._playerId, newConfigs).then(() => this);
    }

    subscribe(playerExtension) {
        const { eventBus, events } = playerExtension;
        events.forEach((value, key) => {
            // TODO: check if the listeners are cleaned up by `deletePlayer`
            eventBus.on(value, event => {
                this.emit(key, event);
                console.log('Event Received', key, event); // eslint-disable-line no-console
            });
        });
        this._unsubscribeEvents = () => {
            eventBus.removeAllListeners();
        };
    }

    unsubscribe() {
        if (this._unsubscribeEvents) {
            this._unsubscribeEvents();
            this._unsubscribeEvents = null;
        }
    }
}

BAMPlayer.loader = new Loader();

BAMPlayer.SDKPromises = {};
BAMPlayer.SDKData = {};

BAMPlayer.Events = {
    LOGIN_REQUESTED: 'LOGIN_REQUESTED',
    SUBSCRIPTIONS_READY: 'SUBSCRIPTIONS_READY',
    GEOLOCATION_READY: 'GEOLOCATION_READY',
    AUTHORIZE_USER: 'AUTHORIZE_USER',
    EMBED_PLAYER_READY: 'EMBED_PLAYER_READY',
    MEDIA_LOADED: 'MEDIA_LOADED',
    MEDIA_PAUSED: 'MEDIA_PAUSED',
    MEDIA_RESUME: 'MEDIA_RESUME',
    MEDIA_MUTE: 'MEDIA_MUTE',
    MEDIA_UNMUTE: 'MEDIA_UNMUTE',
    PLAYBACK_ERROR: 'PLAYBACK_ERROR', // TODO - to be deprecated
    PLAYBACK_INITIALIZED: 'PLAYBACK_INITIALIZED',
    FEED_CHANGE_REQUEST: 'FEED_CHANGE_REQUEST',
    FEED_CHANGED: 'FEED_CHANGED',
    SETTINGS_CHANGED: 'SETTINGS_CHANGED',
    PLAYER_CONFIGS_RETURNED: 'PLAYER_CONFIGS_RETURNED',
    PLAYER_CONFIGS_UPDATED: 'PLAYER_CONFIGS_UPDATED',
    SESSIONTOKEN_READY: 'SESSIONTOKEN_READY',
    SYNDICATED_PLAYER_MUTE: 'SYNDICATED_PLAYER_MUTE',
    SYNDICATED_PLAYER_UNMUTE: 'SYNDICATED_PLAYER_UNMUTE',
    SYNDICATED_PLAYER_PLAY: 'SYNDICATED_PLAYER_PLAY',
    SYNDICATED_PLAYER_PAUSE: 'SYNDICATED_PLAYER_PAUSE',
    SYNDICATED_PLAYER_ADD_TO_PLAYLIST: 'SYNDICATED_PLAYER_ADD_TO_PLAYLIST',
    SYNDICATED_PLAYER_REMOVE_FROM_PLAYLIST: 'SYNDICATED_PLAYER_REMOVE_FROM_PLAYLIST',
    SYNDICATED_PLAYER_CLEAR_PLAYLIST: 'SYNDICATED_PLAYER_CLEAR_PLAYLIST',
    SYNDICATED_PLAYER_PLAYLIST_NEXT: 'SYNDICATED_PLAYER_PLAYLIST_NEXT',
    SYNDICATED_PLAYER_PLAYLIST_PREVIOUS: 'SYNDICATED_PLAYER_PLAYLIST_PREVIOUS',
    SYNDICATED_PLAYER_GET_CONFIGS: 'SYNDICATED_PLAYER_GET_CONFIGS',
    SYNDICATED_PLAYER_UPDATE_CONFIGS: 'SYNDICATED_PLAYER_UPDATE_CONFIGS',
    USER_TRACKING_INFORMATION_READY: 'USER_TRACKING_INFORMATION_READY'
};

export default BAMPlayer;



// WEBPACK FOOTER //
// ./app/webapp/common/video-player/player-tech/BAMPlayer/index.js